import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import {
  BarChart3,
  Phone,
  Clock,
  TrendingUp,
  TrendingDown,
  Users,
  CheckCircle,
  XCircle,
  Flame,
  ThermometerSun,
  Snowflake,
  Loader2,
  Calendar,
  RefreshCw,
  ArrowUp,
  ArrowDown,
  Minus
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const API = `${process.env.REACT_APP_API_URL}/api`;

export default function PerformanceDashboard() {
  const { getAuthHeader } = useAuth();
  
  const [agents, setAgents] = useState([]);
  const [performance, setPerformance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAgent, setSelectedAgent] = useState("all");
  const [timeRange, setTimeRange] = useState("7d");

  useEffect(() => {
    fetchData();
  }, [selectedAgent, timeRange]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [agentsRes, perfRes] = await Promise.all([
        axios.get(`${API}/agents`, { headers: getAuthHeader() }),
        axios.get(`${API}/performance/agents${selectedAgent !== "all" ? `?agent_id=${selectedAgent}` : ""}`, { headers: getAuthHeader() })
      ]);
      
      setAgents(agentsRes.data || []);
      setPerformance(perfRes.data.performance || []);
    } catch (error) {
      console.error("Failed to fetch performance data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate totals
  const totals = performance.reduce((acc, p) => ({
    total_calls: acc.total_calls + (p.total_calls || 0),
    total_duration: acc.total_duration + (p.total_duration_minutes || 0),
    answered: acc.answered + (p.outcomes?.answered || 0),
    no_answer: acc.no_answer + (p.outcomes?.no_answer || 0),
    voicemails: acc.voicemails + (p.outcomes?.voicemails || 0),
    hot_leads: acc.hot_leads + (p.lead_scores?.hot || 0),
    warm_leads: acc.warm_leads + (p.lead_scores?.warm || 0),
    cold_leads: acc.cold_leads + (p.lead_scores?.cold || 0)
  }), {
    total_calls: 0,
    total_duration: 0,
    answered: 0,
    no_answer: 0,
    voicemails: 0,
    hot_leads: 0,
    warm_leads: 0,
    cold_leads: 0
  });

  const connectionRate = totals.total_calls > 0 
    ? ((totals.answered / totals.total_calls) * 100).toFixed(1)
    : 0;
  
  const conversionRate = totals.total_calls > 0
    ? (((totals.hot_leads + totals.warm_leads) / totals.total_calls) * 100).toFixed(1)
    : 0;

  const avgCallDuration = totals.total_calls > 0
    ? (totals.total_duration / totals.total_calls).toFixed(1)
    : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="performance-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-heading font-bold tracking-tight mb-2">
            Agent Performance
          </h1>
          <p className="text-muted-foreground">
            Track and analyze your AI agents' calling performance
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedAgent} onValueChange={setSelectedAgent}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All Agents" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Agents</SelectItem>
              {agents.map((agent) => (
                <SelectItem key={agent.id} value={agent.id}>
                  {agent.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="24h">Last 24 hours</SelectItem>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={fetchData}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Phone className="w-6 h-6 text-primary" />
              </div>
              <Badge variant="outline" className="text-emerald-400">
                <ArrowUp className="w-3 h-3 mr-1" />
                12%
              </Badge>
            </div>
            <p className="text-3xl font-bold mb-1">{totals.total_calls}</p>
            <p className="text-sm text-muted-foreground">Total Calls</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-emerald-400" />
              </div>
              <Badge variant="outline" className={parseFloat(connectionRate) >= 50 ? "text-emerald-400" : "text-amber-400"}>
                {connectionRate}%
              </Badge>
            </div>
            <p className="text-3xl font-bold mb-1">{totals.answered}</p>
            <p className="text-sm text-muted-foreground">Calls Connected</p>
            <Progress value={parseFloat(connectionRate)} className="h-1 mt-2" />
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center">
                <Flame className="w-6 h-6 text-red-400" />
              </div>
              <Badge variant="outline" className="text-red-400">
                {conversionRate}%
              </Badge>
            </div>
            <p className="text-3xl font-bold mb-1">{totals.hot_leads + totals.warm_leads}</p>
            <p className="text-sm text-muted-foreground">Qualified Leads</p>
            <div className="flex items-center gap-2 mt-2">
              <Badge className="bg-red-500/20 text-red-400 text-xs">
                <Flame className="w-3 h-3 mr-1" />
                {totals.hot_leads} hot
              </Badge>
              <Badge className="bg-amber-500/20 text-amber-400 text-xs">
                <ThermometerSun className="w-3 h-3 mr-1" />
                {totals.warm_leads} warm
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
                <Clock className="w-6 h-6 text-blue-400" />
              </div>
            </div>
            <p className="text-3xl font-bold mb-1">{avgCallDuration}</p>
            <p className="text-sm text-muted-foreground">Avg Call Duration (min)</p>
            <p className="text-xs text-muted-foreground mt-2">
              Total: {totals.total_duration.toFixed(0)} minutes
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Call Outcomes Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Call Outcomes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                    Answered
                  </span>
                  <span className="text-sm font-medium">{totals.answered}</span>
                </div>
                <Progress 
                  value={totals.total_calls > 0 ? (totals.answered / totals.total_calls) * 100 : 0} 
                  className="h-2 bg-muted [&>div]:bg-emerald-500" 
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm flex items-center gap-2">
                    <XCircle className="w-4 h-4 text-amber-400" />
                    No Answer
                  </span>
                  <span className="text-sm font-medium">{totals.no_answer}</span>
                </div>
                <Progress 
                  value={totals.total_calls > 0 ? (totals.no_answer / totals.total_calls) * 100 : 0} 
                  className="h-2 bg-muted [&>div]:bg-amber-500" 
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm flex items-center gap-2">
                    <Phone className="w-4 h-4 text-blue-400" />
                    Voicemail
                  </span>
                  <span className="text-sm font-medium">{totals.voicemails}</span>
                </div>
                <Progress 
                  value={totals.total_calls > 0 ? (totals.voicemails / totals.total_calls) * 100 : 0} 
                  className="h-2 bg-muted [&>div]:bg-blue-500" 
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Lead Quality Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm flex items-center gap-2">
                    <Flame className="w-4 h-4 text-red-400" />
                    Hot Leads
                  </span>
                  <span className="text-sm font-medium">{totals.hot_leads}</span>
                </div>
                <Progress 
                  value={totals.answered > 0 ? (totals.hot_leads / totals.answered) * 100 : 0} 
                  className="h-2 bg-muted [&>div]:bg-red-500" 
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm flex items-center gap-2">
                    <ThermometerSun className="w-4 h-4 text-amber-400" />
                    Warm Leads
                  </span>
                  <span className="text-sm font-medium">{totals.warm_leads}</span>
                </div>
                <Progress 
                  value={totals.answered > 0 ? (totals.warm_leads / totals.answered) * 100 : 0} 
                  className="h-2 bg-muted [&>div]:bg-amber-500" 
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm flex items-center gap-2">
                    <Snowflake className="w-4 h-4 text-blue-400" />
                    Cold Leads
                  </span>
                  <span className="text-sm font-medium">{totals.cold_leads}</span>
                </div>
                <Progress 
                  value={totals.answered > 0 ? (totals.cold_leads / totals.answered) * 100 : 0} 
                  className="h-2 bg-muted [&>div]:bg-blue-500" 
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Agent Performance Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Performance by Agent
          </CardTitle>
          <CardDescription>
            Detailed breakdown of each agent's performance metrics
          </CardDescription>
        </CardHeader>
        <CardContent>
          {performance.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No performance data yet</p>
              <p className="text-sm">Start making calls to see agent performance</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Agent</TableHead>
                  <TableHead className="text-right">Calls</TableHead>
                  <TableHead className="text-right">Connected</TableHead>
                  <TableHead className="text-right">Connection Rate</TableHead>
                  <TableHead className="text-right">Hot Leads</TableHead>
                  <TableHead className="text-right">Conversion Rate</TableHead>
                  <TableHead className="text-right">Avg Duration</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {performance.map((perf) => {
                  const agent = agents.find(a => a.id === perf.agent_id);
                  return (
                    <TableRow key={perf.agent_id}>
                      <TableCell className="font-medium">
                        {agent?.name || "Unknown Agent"}
                      </TableCell>
                      <TableCell className="text-right">{perf.total_calls}</TableCell>
                      <TableCell className="text-right">{perf.outcomes?.answered || 0}</TableCell>
                      <TableCell className="text-right">
                        <Badge variant={perf.connection_rate >= 50 ? "default" : "secondary"}>
                          {perf.connection_rate}%
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="flex items-center justify-end gap-1">
                          <Flame className="w-4 h-4 text-red-400" />
                          {perf.lead_scores?.hot || 0}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge variant={perf.conversion_rate >= 20 ? "default" : "secondary"}>
                          {perf.conversion_rate}%
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {perf.avg_call_duration_seconds ? `${(perf.avg_call_duration_seconds / 60).toFixed(1)} min` : "-"}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
